import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CTAButton, GhostButton } from "./primitives";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineReveal} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

const POINTS = [[95, 320], [265, 232], [440, 128], [520, 64]];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero"
      className="relative min-h-screen overflow-hidden bg-gradient-to-b from-white to-[#EDF1E6]"
    >
      <div className="absolute inset-0 hero-grid" />
      <div className="pointer-events-none absolute -right-24 top-24 h-[34rem] w-[34rem] rounded-full bg-brand/25 blur-[150px]" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-brand/10 blur-[130px]" />

      {/* Abstract pipeline / growth-line graphic */}
      <svg
        viewBox="0 0 520 420"
        fill="none"
        aria-hidden
        className="absolute bottom-16 right-4 hidden w-[44%] max-w-2xl lg:block"
      >
        <defs>
          <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8CC63F" stopOpacity="0.28" />
            <stop offset="1" stopColor="#8CC63F" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0 360 L95 320 L180 338 L265 232 L350 262 L440 128 L520 64 L520 420 L0 420 Z" fill="url(#area)" />
        <motion.path
          d="M0 360 L95 320 L180 338 L265 232 L350 262 L440 128 L520 64"
          stroke="#5E9E1F"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.8, delay: 0.5, ease: "easeInOut" }}
        />
        {POINTS.map(([cx, cy], i) => (
          <motion.circle
            key={i}
            cx={cx}
            cy={cy}
            r="6"
            fill="#fff"
            stroke="#5E9E1F"
            strokeWidth="3"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1 + i * 0.2 }}
          />
        ))}
      </svg>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-28 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#5C8A1E]" />
          <span className="overline">GTM Strategy · Revenue Consulting · Recruitment</span>
        </motion.div>

        <h1 className="max-w-4xl font-serif text-[2.6rem] font-semibold leading-[1.05] tracking-tight text-[#1b1f17] sm:text-6xl lg:text-7xl">
          <Line i={0}>Build a GTM Engine</Line>
          <Line i={1}>
            That Closes <span className="text-[#5E9E1F]">Enterprise Deals</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-[#4c5445] sm:text-lg"
        >
          GTM strategy, revenue operations, and sales execution built to help ambitious firms
          scale confidently into global markets — plus cross-functional recruitment services
          across engineering, product, operations, leadership, and sales.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <CTAButton testId="hero-cta" href="#contact">
            Book a Discovery Call <ArrowRight className="h-4 w-4" />
          </CTAButton>
          <GhostButton testId="hero-secondary" href="#framework">
            Explore the Framework
          </GhostButton>
        </motion.div>
      </div>
    </section>
  );
};
